import { useState, useEffect, Component, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import styles from './AssessmentsPage.module.css'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'
const API_HOST = API_BASE_URL.replace(/\/api\/v1\/?$/, '')

// ============================================================================
// Error Boundary - Catches any render errors
// ============================================================================
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean, error: Error | null }> {
    constructor(props: { children: ReactNode }) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        console.error('AssessmentsPage Error:', error, info)
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: '2rem', background: 'white', minHeight: '100vh' }}>
                    <h1>Something went wrong</h1>
                    <p>Error: {this.state.error?.message}</p>
                    <Link to="/dashboard">← Back to Dashboard</Link>
                </div>
            )
        }
        return this.props.children
    }
}

// ============================================================================
// Types
// ============================================================================
interface Question {
    id: number
    text: string
    question_type: 'scale' | 'text'
    order: number
}

interface Questionnaire {
    id: number
    title: string
    description: string
    category: string
    questions?: Question[]
}

// Default questionnaires - shown immediately, updated from API if available
const DEFAULT_QUESTIONNAIRES: Questionnaire[] = [
    { id: 1, title: 'Anxiety Assessment', description: 'Evaluate your anxiety levels and patterns.', category: 'anxiety' },
    { id: 2, title: 'Marriage & Relationship', description: 'Assess your relationship dynamics.', category: 'marriage_relationship' },
    { id: 3, title: 'Emotional Safety', description: 'Check in on your emotional wellbeing.', category: 'emotional_safety' }
]

// ============================================================================
// Main Component
// ============================================================================
interface AssessmentHistory {
    id: number
    questionnaire_id: number
    questionnaire_title: string
    questionnaire_category: string
    completed_at: string | null
    report_status: string
    report_summary: string | null
    report_file: string | null
}

function AssessmentsPageContent() {
    // State
    const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>(DEFAULT_QUESTIONNAIRES)
    const [history, setHistory] = useState<AssessmentHistory[]>([])
    const [showModal, setShowModal] = useState(false)
    const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<Questionnaire | null>(null)
    const [questions, setQuestions] = useState<Question[]>([])
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
    const [answers, setAnswers] = useState<Record<number, string | number>>({})
    const [submitting, setSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [loadingQuestions, setLoadingQuestions] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Report modal state
    const [showReportModal, setShowReportModal] = useState(false)
    const [selectedReport, setSelectedReport] = useState<AssessmentHistory | null>(null)

    // Fetch questionnaires and history on mount
    useEffect(() => {
        let mounted = true

        const fetchData = async () => {
            try {
                const token = localStorage.getItem('access_token')
                if (!token) return

                // Fetch questionnaires
                const qRes = await fetch(`${API_BASE_URL}/questionnaires/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                if (qRes.ok && mounted) {
                    const data = await qRes.json()
                    if (Array.isArray(data) && data.length > 0) {
                        setQuestionnaires(data)
                    }
                }

                // Fetch user's assessment history
                const hRes = await fetch(`${API_BASE_URL}/questionnaires/my-history/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                if (hRes.ok && mounted) {
                    const historyData = await hRes.json()
                    setHistory(historyData)
                }
            } catch (err) {
                console.error('Error fetching data:', err)
            }
        }

        fetchData()
        return () => { mounted = false }
    }, [submitted]) // Refetch when submission completes

    // Check if user has completed a questionnaire
    const hasCompletedQuestionnaire = (questionnaireId: number): boolean => {
        return history.some(h => h.questionnaire_id === questionnaireId)
    }

    // Helper functions
    const getCategoryIcon = (category: string): string => {
        const icons: Record<string, string> = {
            'anxiety': '🌿',
            'marriage_relationship': '💛',
            'emotional_safety': '❤️‍🩹'
        }
        return icons[category] || '📋'
    }

    const formatDate = (dateStr: string | null): string => {
        if (!dateStr) return 'N/A'
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: 'numeric', month: 'short', year: 'numeric'
        })
    }

    const getStatusBadgeClass = (status: string): string => {
        switch (status) {
            case 'completed': return styles.statusCompleted
            case 'in_progress': return styles.statusInProgress
            default: return styles.statusPending
        }
    }

    // View report handler
    const viewReport = (report: AssessmentHistory) => {
        setSelectedReport(report)
        setShowReportModal(true)
    }

    // Start assessment - load questions
    const startAssessment = async (questionnaire: Questionnaire) => {
        setError(null)
        setLoadingQuestions(true)
        setSelectedQuestionnaire(questionnaire)
        setCurrentQuestionIndex(0)
        setAnswers({})
        setSubmitted(false)
        setShowModal(true)

        try {
            const token = localStorage.getItem('access_token')
            const res = await fetch(`${API_BASE_URL}/questionnaires/${questionnaire.id}/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })

            if (res.ok) {
                const data = await res.json()
                const questionsList = data.questions || []
                // Sort questions by order
                questionsList.sort((a: Question, b: Question) => a.order - b.order)
                setQuestions(questionsList)
            } else {
                const errorData = await res.json()
                setError(errorData.detail || 'Failed to load questions')
            }
        } catch (err) {
            console.error('Error loading questions:', err)
            setError('Network error loading questions')
        } finally {
            setLoadingQuestions(false)
        }
    }

    // Handle answer selection
    const handleAnswer = (questionId: number, value: string | number) => {
        setAnswers(prev => ({ ...prev, [questionId]: value }))
    }

    // Navigation
    const nextQuestion = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1)
        }
    }

    const prevQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1)
        }
    }

    // Submit assessment
    const submitAssessment = async () => {
        if (!selectedQuestionnaire) return

        setSubmitting(true)
        setError(null)

        try {
            const token = localStorage.getItem('access_token')
            if (!token) {
                setError('Not authenticated')
                return
            }

            // Format answers with string keys
            const formattedAnswers: Record<string, string | number> = {}
            Object.entries(answers).forEach(([key, value]) => {
                formattedAnswers[String(key)] = value
            })

            const requestBody = {
                questionnaire_id: selectedQuestionnaire.id,
                answers: formattedAnswers
            }

            console.log('Submitting:', requestBody)

            const res = await fetch(`${API_BASE_URL}/questionnaires/submit-response/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            })

            const responseData = await res.json()
            console.log('Response:', res.status, responseData)

            if (res.ok) {
                setSubmitted(true)
            } else {
                // Extract error message from various possible formats
                let errorMsg = 'Submission failed'
                if (responseData.error) {
                    errorMsg = typeof responseData.error === 'string'
                        ? responseData.error
                        : JSON.stringify(responseData.error)
                } else if (responseData.detail) {
                    errorMsg = responseData.detail
                }
                setError(errorMsg)
                alert(`Failed: ${errorMsg}`)
            }
        } catch (err) {
            console.error('Submit error:', err)
            setError('Network error during submission')
            alert('Network error. Please try again.')
        } finally {
            setSubmitting(false)
        }
    }

    // Close modal
    const closeModal = () => {
        setShowModal(false)
        setSelectedQuestionnaire(null)
        setQuestions([])
        setAnswers({})
        setCurrentQuestionIndex(0)
        setError(null)
    }

    // Current question helper
    const currentQuestion = questions[currentQuestionIndex]
    const isLastQuestion = currentQuestionIndex === questions.length - 1
    const canSubmit = Object.keys(answers).length >= questions.length

    // ============================================================================
    // Render
    // ============================================================================
    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <Link to="/dashboard" className={styles.backButton}>← Back to Dashboard</Link>
                <h1 className={styles.title}>Wellness Assessments</h1>
            </div>

            <div className={styles.content}>
                {/* Available Assessments */}
                <section className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <span className={styles.sectionIcon}>📋</span>
                        <h2 className={styles.sectionTitle}>Available Assessments</h2>
                    </div>
                    <p className={styles.sectionDescription}>
                        Choose an assessment to gain insights into your mental wellness.
                    </p>

                    <div className={styles.assessmentsGrid}>
                        {questionnaires.map(q => (
                            <div key={q.id} className={styles.assessmentCard}>
                                {hasCompletedQuestionnaire(q.id) && (
                                    <div className={styles.completedBadge}>✓ Completed</div>
                                )}
                                <div className={styles.cardIcon}>
                                    <span style={{ fontSize: '2.5rem' }}>{getCategoryIcon(q.category)}</span>
                                </div>
                                <h3 className={styles.cardTitle}>{q.title}</h3>
                                <p className={styles.cardDescription}>{q.description}</p>
                                <div className={styles.cardMeta}>
                                    <span>📝 10 questions</span>
                                    <span>⏱️ ~5 minutes</span>
                                </div>
                                <button
                                    className={styles.startButton}
                                    onClick={() => startAssessment(q)}
                                >
                                    {hasCompletedQuestionnaire(q.id) ? 'Retake Assessment →' : 'Start Assessment →'}
                                </button>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Assessment History */}
                {history.length > 0 && (
                    <section className={styles.section}>
                        <div className={styles.sectionHeader}>
                            <span className={styles.sectionIcon}>📊</span>
                            <h2 className={styles.sectionTitle}>My Assessment History</h2>
                        </div>
                        <p className={styles.sectionDescription}>
                            Review your previous assessments and reports.
                        </p>

                        <div className={styles.historyList}>
                            {history.map(h => (
                                <div key={h.id} className={styles.historyCard}>
                                    <div className={styles.historyIcon}>
                                        <span style={{ fontSize: '1.5rem' }}>{getCategoryIcon(h.questionnaire_category)}</span>
                                    </div>
                                    <div className={styles.historyDetails}>
                                        <h4 className={styles.historyTitle}>{h.questionnaire_title}</h4>
                                        <p className={styles.historyDate}>Completed: {formatDate(h.completed_at)}</p>
                                    </div>
                                    <div className={`${styles.statusBadge} ${getStatusBadgeClass(h.report_status)}`}>
                                        {h.report_status === 'completed' ? '📄 Report Ready' :
                                            h.report_status === 'in_progress' ? '⏳ Under Review' : '🕐 Pending Review'}
                                    </div>
                                    {h.report_status === 'completed' && (
                                        <button
                                            className={styles.viewReportButton}
                                            onClick={() => viewReport(h)}
                                        >
                                            View Report →
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Safety Note */}
                <div className={styles.safetyNote}>
                    <strong>⚠️ Note:</strong> These are wellness check-ins, not clinical diagnoses.
                </div>
            </div>

            {/* Modal */}
            {showModal && selectedQuestionnaire && (
                <div className={styles.modalOverlay} onClick={closeModal}>
                    <div className={styles.modal} onClick={e => e.stopPropagation()}>
                        {/* Success Screen */}
                        {submitted ? (
                            <div className={styles.successScreen}>
                                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
                                <h2>Assessment Submitted!</h2>
                                <p>Thank you for completing the {selectedQuestionnaire.title}.</p>
                                <p style={{ color: '#888', marginTop: '1rem' }}>
                                    A psychologist will review your responses and prepare a personalized report.
                                </p>
                                <button className={styles.startButton} onClick={closeModal} style={{ marginTop: '2rem' }}>
                                    Back to Assessments
                                </button>
                            </div>
                        ) : loadingQuestions ? (
                            /* Loading State */
                            <div className={styles.successScreen}>
                                <div className={styles.spinner}></div>
                                <p>Loading questions...</p>
                            </div>
                        ) : error && questions.length === 0 ? (
                            /* Error State */
                            <div className={styles.successScreen}>
                                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
                                <p style={{ color: 'red' }}>{error}</p>
                                <button className={styles.startButton} onClick={closeModal} style={{ marginTop: '1rem' }}>
                                    Close
                                </button>
                            </div>
                        ) : questions.length === 0 ? (
                            /* No Questions */
                            <div className={styles.successScreen}>
                                <p>No questions available for this assessment.</p>
                                <button className={styles.startButton} onClick={closeModal} style={{ marginTop: '1rem' }}>
                                    Close
                                </button>
                            </div>
                        ) : (
                            /* Question Display */
                            <>
                                <div className={styles.modalHeader}>
                                    <h2>{selectedQuestionnaire.title}</h2>
                                    <button className={styles.closeButton} onClick={closeModal}>✕</button>
                                </div>

                                {/* Progress Bar */}
                                <div className={styles.progressBar}>
                                    <div
                                        className={styles.progressFill}
                                        style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                                    />
                                </div>
                                <p className={styles.progressText}>
                                    Question {currentQuestionIndex + 1} of {questions.length}
                                </p>

                                {/* Question */}
                                {currentQuestion && (
                                    <div className={styles.questionContainer}>
                                        <p className={styles.questionText}>{currentQuestion.text}</p>

                                        {currentQuestion.question_type === 'scale' ? (
                                            <div className={styles.scaleOptions}>
                                                {[1, 2, 3, 4, 5].map(value => (
                                                    <button
                                                        key={value}
                                                        className={`${styles.scaleButton} ${answers[currentQuestion.id] === value ? styles.selected : ''}`}
                                                        onClick={() => handleAnswer(currentQuestion.id, value)}
                                                    >
                                                        <span className={styles.scaleNumber}>{value}</span>
                                                        <span className={styles.scaleLabel}>
                                                            {value === 1 && 'Very Rarely'}
                                                            {value === 2 && 'Sometimes'}
                                                            {value === 3 && 'Often'}
                                                            {value === 4 && 'Very Often'}
                                                            {value === 5 && 'Always'}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        ) : (
                                            <textarea
                                                className={styles.textAnswer}
                                                placeholder="Share your thoughts..."
                                                value={(answers[currentQuestion.id] as string) || ''}
                                                onChange={e => handleAnswer(currentQuestion.id, e.target.value)}
                                                rows={4}
                                            />
                                        )}
                                    </div>
                                )}

                                {/* Error display */}
                                {error && (
                                    <div style={{ padding: '0 2rem', color: 'red', textAlign: 'center' }}>
                                        {error}
                                    </div>
                                )}

                                {/* Footer Navigation */}
                                <div className={styles.modalFooter}>
                                    <button
                                        className={styles.navButton}
                                        onClick={prevQuestion}
                                        disabled={currentQuestionIndex === 0}
                                    >
                                        ← Previous
                                    </button>

                                    {isLastQuestion ? (
                                        <button
                                            className={styles.startButton}
                                            onClick={submitAssessment}
                                            disabled={submitting || !canSubmit}
                                        >
                                            {submitting ? 'Submitting...' : 'Submit Assessment'}
                                        </button>
                                    ) : (
                                        <button
                                            className={styles.navButton}
                                            onClick={nextQuestion}
                                            disabled={answers[currentQuestion?.id] === undefined}
                                        >
                                            Next →
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Report Modal */}
            {showReportModal && selectedReport && (
                <div className={styles.modalOverlay} onClick={() => setShowReportModal(false)}>
                    <div className={styles.reportModal} onClick={e => e.stopPropagation()}>
                        <div className={styles.reportHeader}>
                            <div>
                                <span className={styles.reportIcon}>{getCategoryIcon(selectedReport.questionnaire_category)}</span>
                                <h2>{selectedReport.questionnaire_title}</h2>
                            </div>
                            <button
                                className={styles.closeButton}
                                onClick={() => setShowReportModal(false)}
                            >
                                ✕
                            </button>
                        </div>

                        <div className={styles.reportContent}>
                            <div className={styles.reportMeta}>
                                <p><strong>Completed:</strong> {formatDate(selectedReport.completed_at)}</p>
                                <p><strong>Status:</strong> <span className={styles.completedLabel}>Report Ready</span></p>
                            </div>

                            {selectedReport.report_summary && (
                                <div className={styles.reportSummary}>
                                    <h3>Report Summary</h3>
                                    <p>{selectedReport.report_summary}</p>
                                </div>
                            )}

                            {selectedReport.report_file ? (
                                <div className={styles.reportActions}>
                                    <a
                                        href={`${API_HOST}${selectedReport.report_file}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.downloadButton}
                                    >
                                        📥 Download Full Report
                                    </a>
                                </div>
                            ) : (
                                <div className={styles.noReportFile}>
                                    <p>📄 {selectedReport.report_summary ? 'Report summary is available above.' : 'Your report is ready.'} No PDF file was uploaded by the psychologist.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

// ============================================================================
// Export with Error Boundary
// ============================================================================
export default function AssessmentsPage() {
    return (
        <ErrorBoundary>
            <AssessmentsPageContent />
        </ErrorBoundary>
    )
}
